import { useState, type ReactNode } from "react";
import { Button, Panel, Row, Skeleton, Stat } from "../../components/ui";
import { claim } from "../../state/actions";
import { dollars, usePaused, type Me } from "./model";

function reasonNot(me: Me, paused: boolean): string | null {
  if (me.state === "disconnected") return "Connect a wallet";
  if (me.state === "loading") return "Loading your account";
  if (paused) return "Claiming is paused";
  if (me.pending.some((operation) => operation.action === "paperClaim")) return "Claim waiting for the next batch";
  if (me.claimable === null) return me.claimableFailed ? "Could not read your cut" : "Loading your cut";
  if (me.claimable === 0n) return "Nothing to claim";
  return null;
}

/** The USDC staking has earned you, and the one button that moves it to your bankroll. */
export function ClaimPanel({ me }: { me: Me }) {
  const [busy, setBusy] = useState(false);
  const paused = usePaused("paperClaim");
  const reason = reasonNot(me, paused);

  let figure: ReactNode = "—";
  if (me.state === "loading" || (me.state === "ready" && me.claimable === null && !me.claimableFailed)) {
    figure = <Skeleton width={120} height={28} />;
  } else if (me.state === "ready" && me.claimable !== null) {
    figure = dollars(me.claimable);
  }
  const earning = me.state === "ready" && me.claimable !== null && me.claimable > 0n;

  async function submit() {
    if (reason !== null || busy) return;
    setBusy(true);
    try {
      await claim();
    } catch {
      // told by a toast
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Your cut" className="stk-panel">
      <Stat label="Claimable USDC" value={figure} size="lg" tone={earning ? "up" : undefined} />

      <div className="stk-rows">
        <Row label="Claimed to date">
          {me.state === "ready" ? dollars(me.claimed) : me.state === "loading" ? <Skeleton width={64} height={16} /> : "—"}
        </Row>
      </div>

      {me.state === "ready" && me.staked === 0n && !earning && (
        <p className="muted stk-small">Stake PAPER to take a cut of the house&apos;s excess and fees.</p>
      )}

      <div className="stk-push stack">
        <Button tone={reason === null ? "primary" : "secondary"} size="lg" block busy={busy} disabled={reason !== null} onClick={submit}>
          {reason ?? "Claim to bankroll"}
        </Button>
        <p className="note">USDC goes to your bankroll. Your wallet signs, no gas.</p>
      </div>
    </Panel>
  );
}
